/**
 * The DRep-side half of the profile ↔ proposal link graph.
 *
 * This used to render as a second "Voting record" list below the profile,
 * because the profile's own Votes tab is client-fetched through RTK Query and
 * therefore invisible to a crawler — and it duplicated, for the reader, a list
 * they could already see. Instead the server resolves the rows here and hands
 * them to that same tab, which renders them as its initial content.
 *
 * It also fixes what the tab shows a person: the vote feed carries no proposal
 * title for most votes (the "-" in the GovAction column), and the id it does
 * carry is `<txHash>#<index>` rather than the bech32 `gov_action1…` the URL
 * uses. Both come from the proposal index — see `proposalByGovActionId`.
 */

import { fetchDRepVotes, proposalByGovActionId } from '@src/lib/seo/catalog';
import { proposalTypeLabel } from '@src/lib/seo/proposal';

export interface SeoVoteRow {
  govActionId: string;
  title?: string | null;
  voteType: string;
  govActionType?: string | null;
  time?: string | null;
}

/** Canonical URL and display title for one governance action. */
export interface ProposalLink {
  href: string;
  title: string;
}

export interface SeoVotes {
  /** First page of votes, server-rendered so the tab has content without JS. */
  rows: SeoVoteRow[];
  total: number;
  /** Keyed by the `<txHash>#<index>` the vote feed uses. */
  links: Record<string, ProposalLink>;
}

export const EMPTY_SEO_VOTES: SeoVotes = { rows: [], total: 0, links: {} };

export async function drepSeoVotes(
  drepIdHex?: string | null,
  limit = 10
): Promise<SeoVotes> {
  if (!drepIdHex) return EMPTY_SEO_VOTES;

  const [page, byGovActionId] = await Promise.all([
    fetchDRepVotes(drepIdHex).catch(() => ({ items: [], total: 0 })),
    proposalByGovActionId().catch(() => new Map()),
  ]);

  const links: Record<string, ProposalLink> = {};
  for (const vote of page.items) {
    const proposal = byGovActionId.get(vote.govActionId);
    if (!proposal) continue;
    links[vote.govActionId] = {
      href: `/governance_actions/${proposal.id}`,
      title:
        proposal.meta?.title?.trim() ||
        vote.title?.trim() ||
        proposalTypeLabel(proposal.proposal?.type),
    };
  }

  return {
    rows: page.items.slice(0, limit).map((vote) => ({
      govActionId: vote.govActionId,
      title: vote.title,
      voteType: vote.voteType,
      govActionType: vote.govActionType,
      time: vote.time,
    })),
    total: page.total,
    links,
  };
}
